import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { loadSettings } from '@/config/settings';
import { resetProgress } from '@/config/progress';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { AudioManager } from '@/systems/AudioManager';

export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Start ambient music if not already playing
    this.startAmbientMusic();

    // Arcade background
    drawArcadeBackground(this);

    // Hero panel (keeps structure without the triangle shards)
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 10, 760, 380, THEME.colors.backgroundLightHex, 0.8);
    panel.setStrokeStyle(3, THEME.colors.magentaHex);
    panel.setDepth(-1);

    // Title with glow
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 3, 'MORSE CODE HERO', {
      fontFamily: THEME.fonts.heading,
      fontSize: '56px',
      color: THEME.colors.cyan,
    });
    title.setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 20);

    // Subtitle
    const subtitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 3 + 60, 'DOT · DASH · DOMINATE', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    });
    subtitle.setOrigin(0.5);

    // Start button (magenta - primary action)
    this.createButton(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 30,
      'START GAME',
      () => this.transitionTo('LevelSelectScene'),
      'primary'
    );

    // Settings button (outlined - secondary action)
    this.createButton(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 100,
      'SETTINGS',
      () => this.transitionTo('SettingsScene'),
      'secondary'
    );

    // Credits button (outlined - secondary action)
    this.createButton(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 155,
      'CREDITS',
      () => this.transitionTo('CreditsScene'),
      'secondary'
    );

    // Reset progress button (small, bottom corner)
    this.createButton(
      GAME_WIDTH - 100,
      GAME_HEIGHT - 40,
      'RESET',
      () => this.showResetConfirmation(),
      'danger'
    );

    // Key hints with actual bindings
    const settings = loadSettings();
    const keyHintContainer = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT - 80);
    const dotText = this.add.text(-100, 0, `[${settings.dotKey}] / LMB`, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
    const dotIcon = this.add.image(-90, 0, 'dot-sprite').setOrigin(0, 0.5).setScale(0.7);
    const dashText = this.add.text(70, 0, `[${settings.dashKey}] / RMB`, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
    const dashIcon = this.add.image(80, 0, 'dash-sprite').setOrigin(0, 0.5).setScale(0.7);
    keyHintContainer.add([dotText, dotIcon, dashText, dashIcon]);

    // Animate title
    this.tweens.add({
      targets: title,
      y: title.y - 10,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createButton(x: number, y: number, text: string, onClick: () => void, style: 'primary' | 'secondary' | 'danger' = 'primary'): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const isPrimary = style === 'primary';
    const isDanger = style === 'danger';
    const width = isDanger ? 100 : isPrimary ? 250 : 180;
    const height = isDanger ? 30 : isPrimary ? 60 : 45;
    const fontSize = isDanger ? THEME.sizes.tiny : isPrimary ? THEME.sizes.bodyLarge : THEME.sizes.body;

    // Primary: filled magenta, Secondary: outlined with cyan, Danger: muted red
    let fillColor: number, strokeColor: number, textColor: string, hoverFill: number;
    if (isDanger) {
      fillColor = THEME.colors.backgroundPanelHex;
      strokeColor = 0x994444;
      textColor = '#aa6666';
      hoverFill = 0x442222;
    } else if (isPrimary) {
      fillColor = THEME.colors.magentaHex;
      strokeColor = THEME.colors.magentaDarkHex;
      textColor = THEME.colors.text;
      hoverFill = THEME.colors.magentaDarkHex;
    } else {
      fillColor = THEME.colors.backgroundLightHex;
      strokeColor = THEME.colors.cyanHex;
      textColor = THEME.colors.cyan;
      hoverFill = THEME.colors.backgroundPanelHex;
    }

    const bg = this.add.rectangle(0, 0, width, height, fillColor, 1);
    bg.setStrokeStyle(THEME.button.borderWidth, strokeColor);

    const label = this.add.text(0, 0, text, {
      fontFamily: THEME.fonts.heading,
      fontSize,
      color: textColor,
    });
    label.setOrigin(0.5);

    container.add([bg, label]);

    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setFillStyle(hoverFill);
      this.tweens.add({
        targets: container,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: THEME.animation.fast,
      });
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(fillColor);
      this.tweens.add({
        targets: container,
        scaleX: 1,
        scaleY: 1,
        duration: THEME.animation.fast,
      });
    });

    bg.on('pointerdown', onClick);

    return container;
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }

  private showResetConfirmation(): void {
    // Create modal overlay
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);
    overlay.setDepth(100);

    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 400, 180, THEME.colors.backgroundPanelHex, 1);
    panel.setStrokeStyle(2, THEME.colors.magentaHex);
    panel.setDepth(101);

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, 'RESET PROGRESS?', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5).setDepth(102);

    const subtitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 15, 'All levels and high scores\nwill be cleared.', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
      align: 'center',
    }).setOrigin(0.5).setDepth(102);

    const closeModal = () => {
      overlay.destroy();
      panel.destroy();
      title.destroy();
      subtitle.destroy();
      confirmBtn.destroy();
      confirmText.destroy();
      cancelBtn.destroy();
      cancelText.destroy();
    };

    // Confirm button
    const confirmBtn = this.add.rectangle(GAME_WIDTH / 2 - 70, GAME_HEIGHT / 2 + 50, 100, 40, 0x993333, 1);
    confirmBtn.setStrokeStyle(2, 0x662222);
    confirmBtn.setDepth(102);
    const confirmText = this.add.text(GAME_WIDTH / 2 - 70, GAME_HEIGHT / 2 + 50, 'RESET', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    }).setOrigin(0.5).setDepth(103);

    confirmBtn.setInteractive({ useHandCursor: true });
    confirmBtn.on('pointerover', () => confirmBtn.setFillStyle(0x662222));
    confirmBtn.on('pointerout', () => confirmBtn.setFillStyle(0x993333));
    confirmBtn.on('pointerdown', () => {
      resetProgress();
      closeModal();
    });

    // Cancel button
    const cancelBtn = this.add.rectangle(GAME_WIDTH / 2 + 70, GAME_HEIGHT / 2 + 50, 100, 40, THEME.colors.backgroundLightHex, 1);
    cancelBtn.setStrokeStyle(2, THEME.colors.cyanHex);
    cancelBtn.setDepth(102);
    const cancelText = this.add.text(GAME_WIDTH / 2 + 70, GAME_HEIGHT / 2 + 50, 'CANCEL', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.cyan,
    }).setOrigin(0.5).setDepth(103);

    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerover', () => cancelBtn.setFillStyle(THEME.colors.backgroundPanelHex));
    cancelBtn.on('pointerout', () => cancelBtn.setFillStyle(THEME.colors.backgroundLightHex));
    cancelBtn.on('pointerdown', closeModal);

    // Click overlay to close
    overlay.setInteractive();
    overlay.on('pointerdown', closeModal);
  }

  private startAmbientMusic(): void {
    // Check if ambient music is already playing (persists across scenes)
    const existingMusic = this.sound.get('ambient');
    if (existingMusic && existingMusic.isPlaying) {
      return;
    }

    const audioManager = new AudioManager(this);

    // If audio context is locked, wait for user interaction
    if (this.sound.locked) {
      this.sound.once('unlocked', () => {
        audioManager.playMusic('ambient');
      });
    } else {
      audioManager.playMusic('ambient');
    }
  }
}
